import { gql } from '@apollo/client';
import { Dialog } from '@headlessui/react';
import { useEffect, useMemo, useState } from 'react';
import { getMstProduct } from 'src/graphql/queries';
import { useAppLazyQuery } from 'src/libs/hooks';

export interface MprProduct {
  mpr_product_nm: string | undefined;
  mpr_product_cd: number | undefined;
  mpr_available_kb: number;
  mpr_discount_type: number;
  mpr_maintenance_limit: number;
}

type ModalGuideProductProps = {
  open: boolean | undefined;
  onClose: () => void;
  onClickRow: (product: MprProduct) => void;
};

const ModalGuideProduct = ({ open, onClose, onClickRow }: ModalGuideProductProps) => {
  const [searchMstProduct, { data: mprProductData, loading: isMstProductLoading }] = useAppLazyQuery({
    query: gql(getMstProduct),
  });

  const [mprProduct, setMstProduct] = useState<Array<MprProduct>>([]);
  const [mprProductCd, setMstProductCd] = useState<string>('');
  const [mprProductMn, setMstProductMn] = useState<string>('');
  const header = ['製品コード', '製品名'];

  const onClickItemProduct = (product: MprProduct) => {
    onClickRow(product);
    setMstProduct([]);
    onClose();
  };

  const renderTableData = useMemo(() => {
    if (mprProduct?.length <= 0 || !mprProduct) return;
    return mprProduct.map((product: MprProduct, index: number) => {
      return (
        <tr key={index}>
          <td className="p-[8px] border-[1px] border-solid border-[#ddd]">
            <p>{product?.mpr_product_cd}</p>
          </td>
          <td className="p-[8px] border-[1px] border-solid border-[#ddd] text-blue-600 cursor-pointer">
            <p onClick={() => onClickItemProduct(product)}>{product?.mpr_product_nm}</p>
          </td>
        </tr>
      );
    });
  }, [mprProduct]);

  const renderTableHeader = () => {
    return header.map((key, index) => (
      <th
        className="whitespace-nowrap p-[8px] sticky top-0 border-[1px] border-solid border-[#ddd] pt-[12px] pb-[12px] text-center bg-blue-400 text-[#fff]"
        key={index}
      >
        {key.toUpperCase()}
      </th>
    ));
  };

  const onPressSearch = () => {
    const params = {
      mpr_product_cd: mprProductCd === '' ? null : mprProductCd,
      mpr_product_nm: mprProductMn,
    };
    searchMstProduct({ variables: { input: params } });
  };

  useEffect(() => {
    const listProduct: MprProduct[] = [
      ...(mprProductData?.getMstProduct?.sort((product1: any, product2: any) => {
        return product1.mpr_product_cd - product2.mpr_product_cd;
      }) ?? []),
    ];
    setMstProduct(listProduct);
  }, [mprProductData]);

  return (
    <Dialog open={open} onClose={onClose} className="relative z-50 w-full h-full">
      <div className="fixed w-full h-full inset-0 flex items-center justify-center">
        <>
          <div className="bg-[#00000030] w-full h-full z-[0] inset-0 fixed" />
          <div className="flex fixed bg-white h-4/6 w-1/3 items-center flex-col z-[1] overflow-auto">
            <div className="w-5/6 flex justify-end items-center mt-[10px]">
              <button onClick={onClose}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="w-6 h-6"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="h-[40px] w-4/5 flex justify-center items-center border border-zinc-300 mt-[50px]">
              <div className="h-full w-2/5 flex justify-center items-center bg-blue-400 text-white">製品コード</div>
              <div className="h-full w-3/5 flex justify-center items-center">
                <input
                  className="bg-transparent pl-[5px] h-[40px] w-full"
                  onChange={(event) => {
                    if (event.target.value.length > 8) return;
                    setMstProductCd(event?.target?.value);
                  }}
                  type="number"
                  name="mpr_product_cd"
                  value={mprProductCd}
                  min={1}
                />
              </div>
            </div>
            <div className="h-[40px] w-4/5 flex justify-center items-center border border-zinc-300 mt-[10px]">
              <div className="h-full w-2/5 flex justify-center items-center bg-blue-400 text-white">製品名</div>
              <div className="h-full w-3/5 flex justify-center items-center">
                <input
                  className="bg-transparent pl-[5px] h-[40px] w-full"
                  onChange={(event) => {
                    if (event.target.value.length > 50) return;
                    setMstProductMn(event?.target?.value);
                  }}
                  type="text"
                  name="mpr_product_nm"
                  value={mprProductMn}
                />
              </div>
            </div>
            <div className="h-[40px] w-4/5 flex justify-end items-center mt-[10px] ">
              <button
                onClick={onPressSearch}
                className="border border-zinc-300 h-[40px] w-[100px] justify-center items-center flex"
              >
                <p>検索</p>
              </button>
            </div>
            <div className="overflow-auto max-h-100 w-4/5 mt-[20px] mb-[20px]">
              <table className="text-center border-collapse border-[3px] border-solid border-[#ddd] w-full td:p-[10px]  ">
                <thead>
                  <tr>{renderTableHeader()}</tr>
                </thead>
                <tbody>
                  {isMstProductLoading ? (
                    <tr>
                      <td
                        colSpan={header.length}
                        className="text-center animate-pulse p-[8px] border-[1px] border-solid border-[#fff]"
                      >
                        Loading...
                      </td>
                    </tr>
                  ) : (
                    renderTableData
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      </div>
    </Dialog>
  );
};

export default ModalGuideProduct;
